import type { FormEventHandler, Ref } from 'react';
import Button from 'components/shared/Buttons/BaseButton';
import { CalculatorInput } from 'components/Widget/CalculatorInput/CalculatorInput';
import type { CurrencyFieldProps } from 'components/Widget/shared/useCurrencyFields';
import type { CalculatorFieldErrors } from 'components/Widget/shared/types';
import { BUDGET_SECTIONS, INCOME_FIELD } from './constants';
import type { BudgetSectionKey } from './types';

interface BudgetCalculatorFormProps {
  /** Focus target after leaving results. */
  ref?: Ref<HTMLFormElement>;
  errors: CalculatorFieldErrors;
  fieldProps: CurrencyFieldProps;
  onCalculate: () => void;
  onReset: () => void;
}

const sectionHintId = (key: BudgetSectionKey) => `budget-calculator-${key}-hint`;

export const BudgetCalculatorForm = ({ ref, errors, fieldProps, onCalculate, onReset }: BudgetCalculatorFormProps) => {
  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  return (
    <form
      ref={ref}
      tabIndex={-1}
      aria-label="Budget calculator"
      className="flex flex-col gap-8"
      onSubmit={handleSubmit}>
      {/* Native validation would pre-empt these custom rules. */}
      <CalculatorInput {...fieldProps(INCOME_FIELD, errors[INCOME_FIELD.label])} aria-required />

      {BUDGET_SECTIONS.map(section => (
        <fieldset key={section.key} aria-describedby={sectionHintId(section.key)}>
          <legend className="text-heading-sm">{section.heading}</legend>
          <p id={sectionHintId(section.key)} className="mt-2 text-legal text-content-secondary">
            {section.hint}
          </p>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {section.fields.map(field => (
              <CalculatorInput key={field.label} {...fieldProps(field, errors[field.label])} />
            ))}
          </div>
        </fieldset>
      ))}

      <div className="flex flex-col gap-4 sm:flex-row">
        <Button type="submit">Calculate</Button>
        <Button type="button" variant="outline" onClick={onReset}>
          Reset
        </Button>
      </div>
    </form>
  );
};
